import axios from '@/axios';
// eslint-disable-next-line import/no-extraneous-dependencies
import HandleResponses from '@common/utils/handleApiResponsesV2';
import { search, update, create, remove } from '@/api-factory/mdm';
import { VEHICLE_DETAILS_MDM } from 'pando-constants';


const actions = {
  async search({ commit }, { section, params }) {
    if (params.page === 1) {
      commit('SET_LOADING', true);
    } else {
      commit('paginationCheck', true);
    }
    try {
      let result = [];
      result = await search({ section, params });
      if (result) {
        commit('SET_LIST', result);
      }
      return result;
    } catch (error) {
      return HandleResponses.Status(error);
    } finally {
      commit('paginationCheck', false);
      commit('SET_LOADING', false);
    }
  },
  async list(_, { section }) {
    try {
      const result = await axios(`/${section}`);
      return result && result.data;
    } catch (error) {
      return HandleResponses.Status(error);
    }
  },
  async create({ commit }, { section, data, isMieone }) {
    try {
      let result = {};
      result = await create({ section, data });
      if (result) {
        commit('appendNew', { data: result });
      }
      return result;
    } catch (error) {
      return HandleResponses.Status(error);
    }
  },
  async editVehicleDetails(_, { payload, number }) {
    try {
      const result = await axios.put(`/transporter/vehicledetail/${number}`, { data: payload });
      return result && result.data;
    } catch (error) {
      return HandleResponses.Status(error);
    }
  },
  async update({ commit }, { section, Id, data }) {
    try {
      let result = {};
      result = await update({ section, Id, data });
      if (result) {
        commit('updateExisting', { Id, data: result });
      }
      return result;
    } catch (error) {
      return HandleResponses.Status(error);
    }
  },

  async remove({ commit }, { section, data }) {
    try {
      let result = {};
      result = await remove({ section, data });
      if (result && result.id) {
        commit('removeExisting', { data: result });
      }
      return result;
    } catch (error) {
      return HandleResponses.Status(error);
    }
  },

  async bulkUpload({ commit }, { section, formData, subPath = '', id = null }) {
    commit('setuploadStatus', true);
    try {
      let uploadType = VEHICLE_DETAILS_MDM.VEHICLE_AVAILABILITY_BULK_UPLOAD;
      if (id) {
        // Determine upload type based on id
        formData.append('id', id);
        if (id === 'bulk_upload') {
          uploadType = VEHICLE_DETAILS_MDM.VEHICLE_AVAILABILITY_BULK_UPLOAD;
        } else if (id === VEHICLE_DETAILS_MDM.VEHICLE_DETAIL_BULK_UPLOAD) {
          uploadType = VEHICLE_DETAILS_MDM.VEHICLE_DETAIL_BULK_UPLOAD;
        }
      }
      const result = await axios.post(`/transporter/${section}/bulk_upload/${subPath}?type=${uploadType}`, formData, { headers: {
        'Content-Type': 'multipart/form-data',
      } });
      return result;
    } catch (error) {
      commit('setuploadStatus', false);
      return HandleResponses.Status(error);
    }
  },

  async uploadStatus({ commit }, { section, uploadType }) {
    try {
      let url = `/transporter/${section}/upload_status`;
      if (uploadType) {
        url = `${url}?type=${uploadType}`;
      }
      if (section === 'consignee' && uploadType === null) {
        url = `${url}?type=1`;
      }

      const result = await axios(url);
      if (result.data && result.data.code === 'COMPLETED') {
        commit('setuploadStatus', false);
      }
      return result.data;
    } catch (error) {
      commit('setuploadStatus', false);
      return HandleResponses.Status(error);
    }
  },

  async export({ commit }, { section, params }) {
    commit('setDownloadStatus', true);
    try {
      const result = await axios({ url: '/transporter/export',
        method: 'GET',
        params,
        responseType: 'blob' });
      if (result.size) {
        const url = window.URL.createObjectURL(new Blob([result]));
        const link = document.createElement('a');
        link.href = url;
        const now = new Date().toISOString();
        link.setAttribute('download', `${section}-export-${now}.xlsx`); // or any other extension
        document.body.appendChild(link);
        link.click();
      }
      return (result.size === 0) ? 'EXPORT_VIA_EMAIL' : false;
    } catch (err) {
      return HandleResponses.Status(err);
    } finally {
      commit('setDownloadStatus', false);
    }
  },
  async rejectDownload({ commit }, { section, transactionIds, uploadType = null }) {
    commit('setDownloadStatus', true);
    try {
      const params = { transaction_ids: transactionIds.join(',') };
      if (uploadType) {
        params.type = uploadType;
      }

      const result = await axios({
        url: `/transporter/${section}/rejected/download`,
        method: 'GET',
        params,
        responseType: 'blob',
      });
      if (result) {
        const url = window.URL.createObjectURL(
          new Blob([result], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
          }));
        const link = document.createElement('a');
        link.href = url;
        const now = new Date().toISOString();
        link.setAttribute('download', `${section}-rejectedList-${now}.xlsx`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
      return true;
    } catch (err) {
      return err;
    } finally {
      commit('setDownloadStatus', false);
    }
  },
  async show(_, { section, id }) {
    try {
      const result = await axios(`/${section}/${id}/show`);
      return result.data;
    } catch (error) {
      return HandleResponses.Status(error);
    }
  },
  async getRoles() {
    try {
      const roles = await axios('transporter/roles');
      return roles;
    } catch (error) {
      return HandleResponses.Status(error);
    }
  },
  async getDepots() {
    try {
      const depots = await axios('transporter/depots');
      return depots;
    } catch (error) {
      return HandleResponses.Status(error);
    }
  },

  async getBulkUploadExcel({ commit }, { section }) {
    commit('setDownloadStatus', true);
    try {
      const result = await axios({
        url: `/transporter/${section}/export`,
        method: 'GET',
        responseType: 'blob',
      });
      if (result) {
        const url = window.URL.createObjectURL(new Blob([result], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${section}-bulk-upload-template.xlsx`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
      return true;
    } catch (error) {
      return HandleResponses.Status(error);
    } finally {
      commit('setDownloadStatus', false);
    }
  },

};
export default actions;
